import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import HeroSection from './components/HeroSection';
import MangaCard from './components/MangaCard';
import { mangaList, getHotManga, getNewManga, getCompletedManga, getLatestUpdates } from '@/mocks/manga';

const categories = [
  { label: '全部漫画', icon: 'ri-global-line', slug: 'all', color: 'bg-primary-500' },
  { label: '韩漫', icon: 'ri-heart-line', slug: 'korean', color: 'bg-accent-500' },
  { label: '日漫', icon: 'ri-star-line', slug: 'japanese', color: 'bg-secondary-500' },
  { label: '国漫', icon: 'ri-flag-line', slug: 'chinese', color: 'bg-primary-700' },
];

const hotManga = getHotManga();
const newManga = getNewManga();
const completedManga = getCompletedManga();
const latestUpdates = getLatestUpdates();

export default function Home() {
  return (
    <>
      <Navbar />
      <ScrollToTop />

      <main>
        <HeroSection />

        {/* Category Navigation */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-3 p-5 rounded-xl bg-background-100 hover:bg-background-200/70 transition-all duration-200 group cursor-pointer"
                >
                  <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${cat.color} text-white`}>
                    <i className={`${cat.icon} text-lg`}></i>
                  </div>
                  <div>
                    <span className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors whitespace-nowrap">
                      {cat.label}
                    </span>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      {cat.slug === 'all' ? '浏览全部' : `热门${cat.label}`}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Recommendations */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                  <i className="ri-fire-line text-primary-500 mr-2"></i>
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">大家都在看的精彩漫画</p>
              </div>
              <Link
                to="/category/all"
                className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                查看全部
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 md:gap-6">
              {hotManga.slice(0, 10).map((manga) => (
                <MangaCard key={manga.id} manga={manga} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                  <i className="ri-timer-line text-accent-500 mr-2"></i>
                  最新更新
                </h2>
                <p className="text-sm text-foreground-500 mt-1">每日更新的热门漫画章节</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 md:gap-6">
              {latestUpdates.slice(0, 10).map((manga) => (
                <MangaCard key={`update-${manga.id}`} manga={manga} />
              ))}
            </div>
          </div>
        </section>

        {/* Completed Manga */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                  <i className="ri-check-double-line text-secondary-500 mr-2"></i>
                  已完结精选
                </h2>
                <p className="text-sm text-foreground-500 mt-1">一口气看完的经典漫画</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
              {completedManga.map((manga) => (
                <MangaCard key={`done-${manga.id}`} manga={manga} size="large" />
              ))}
            </div>
          </div>
        </section>

        {/* Feature Highlights */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                为什么选择樱花漫画？
              </h2>
              <p className="text-sm text-foreground-500 mt-2">海量资源 · 高清画质 · 免费阅读</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-stack-line', title: '海量漫画资源', desc: '涵盖韩漫、日漫、国漫三大品类，持续更新中', color: 'text-primary-500' },
                { icon: 'ri-smartphone-line', title: '下拉式阅读', desc: '手机端下拉式流畅阅读体验，无需翻页', color: 'text-accent-500' },
                { icon: 'ri-emotion-happy-line', title: '免费全集', desc: '所有漫画免费全集阅读，无需付费订阅', color: 'text-secondary-500' },
                { icon: 'ri-notification-3-line', title: '追更提醒', desc: '关注喜爱的漫画，第一时间获取更新通知', color: 'text-primary-600' },
              ].map((feature) => (
                <div key={feature.title} className="text-center p-6 rounded-xl bg-background-100">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-200">
                    <i className={`${feature.icon} text-xl ${feature.color}`}></i>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-16 md:py-20 px-4 md:px-6 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20artistic%20manga%20style%20illustration%20with%20warm%20pink%20and%20cream%20gradient%2C%20floating%20cherry%20blossom%20petals%2C%20bookshelf%20silhouette%2C%20cozy%20reading%20atmosphere%2C%20ethereal%20dreamy%20vibe%2C%20clean%20beautiful%20composition&width=1920&height=600&seq=cta-bg-manga-02&orientation=landscape"
            alt="开始你的漫画之旅"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/40 to-black/55"></div>

          <div className="relative z-10 max-w-[1280px] mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
              开始你的漫画之旅
            </h2>
            <p className="text-white/80 text-sm md:text-base mb-8 max-w-lg mx-auto">
              加入樱花漫画，与百万漫友一起畅享韩漫、日漫、国漫的精彩世界
            </p>
            <Link
              to="/category/all"
              className="inline-flex items-center px-10 py-3.5 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-rocket-line mr-2"></i>
              立即探索
            </Link>
          </div>
        </section>
      </main>

      <Footer />

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '樱花漫画 - 韩漫日漫国漫漫画大全免费全集下拉式阅读',
            description: '樱花漫画网提供海量韩漫、日漫、国漫，免费全集下拉式阅读，好看的漫画推荐，在线追更连载收藏，满足漫画爱好者需求。',
            url: 'https://www.yinghuamanhua.com',
            isPartOf: {
              '@type': 'WebSite',
              name: '樱花漫画',
              url: 'https://www.yinghuamanhua.com',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [{
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.yinghuamanhua.com',
              }],
            },
          }),
        }}
      />
    </>
  );
}